import { useCallback, useRef, useState } from "react";

export function useBottomSheetDrag({
  enabled,
  onClose,
  closeThreshold = 90,
}) {
  const startYRef = useRef(0);
  const draggingRef = useRef(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const getPointY = useCallback((event) => {
    if (event?.touches?.length) {
      return event.touches[0].clientY;
    }
    if (event?.changedTouches?.length) {
      return event.changedTouches[0].clientY;
    }
    return event?.clientY ?? 0;
  }, []);

  const resetDrag = useCallback(() => {
    draggingRef.current = false;
    setIsDragging(false);
    setDragOffsetY(0);
  }, []);

  const onPointerDown = useCallback(
    (event) => {
      if (!enabled) {
        return;
      }
      draggingRef.current = true;
      setIsDragging(true);
      startYRef.current = getPointY(event);
      setDragOffsetY(0);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [enabled, getPointY],
  );

  const onPointerMove = useCallback(
    (event) => {
      if (!enabled || !draggingRef.current) {
        return;
      }
      setDragOffsetY(Math.max(0, getPointY(event) - startYRef.current));
    },
    [enabled, getPointY],
  );

  const onPointerUp = useCallback(
    (event) => {
      if (!enabled || !draggingRef.current) {
        return;
      }

      const offset = Math.max(0, getPointY(event) - startYRef.current);
      draggingRef.current = false;
      setIsDragging(false);

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      if (offset > closeThreshold) {
        setDragOffsetY(0);
        onClose();
        return;
      }

      setDragOffsetY(0);
    },
    [closeThreshold, enabled, getPointY, onClose],
  );

  const onTouchStart = useCallback(
    (event) => {
      if (!enabled) {
        return;
      }
      draggingRef.current = true;
      setIsDragging(true);
      startYRef.current = getPointY(event);
      setDragOffsetY(0);
    },
    [enabled, getPointY],
  );

  const onTouchMove = useCallback(
    (event) => {
      if (!enabled || !draggingRef.current) {
        return;
      }
      const offset = Math.max(0, getPointY(event) - startYRef.current);
      setDragOffsetY(offset);
      event.preventDefault();
    },
    [enabled, getPointY],
  );

  const onTouchEnd = useCallback(
    (event) => {
      if (!enabled || !draggingRef.current) {
        return;
      }
      const offset = Math.max(0, getPointY(event) - startYRef.current);
      draggingRef.current = false;
      setIsDragging(false);
      if (offset > closeThreshold) {
        setDragOffsetY(0);
        onClose();
        return;
      }
      setDragOffsetY(0);
    },
    [closeThreshold, enabled, getPointY, onClose],
  );

  const dragHandleProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      padding: "10px 0 6px",
      margin: "0 0 6px",
      boxSizing: "border-box",
      touchAction: "none",
      cursor: "grab",
      flexShrink: 0,
    },
  };

  const sheetMotionStyle = enabled
    ? {
        transform: `translateY(${dragOffsetY}px)`,
        transition: isDragging ? "none" : "transform 220ms ease",
      }
    : {};

  return {
    dragOffsetY,
    isDragging,
    dragHandleProps,
    sheetMotionStyle,
    resetDrag,
  };
}

export const bottomSheetDragHandleVisualStyle = {
  width: 44,
  height: 5,
  borderRadius: 99,
  backgroundColor: "#CFCFCF",
  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
  flexShrink: 0,
};
